import {useState, useMemo, useEffect} from 'react';
import {DistributionList} from "../../../service/types/types";
import {
    Badge,
    Button,
    Card,
    Col,
    FormControl,
    InputGroup,
    Modal,
    Row,
} from 'react-bootstrap';

interface Usuario {
    nombre: string;
    rut: string;
    correo: string;
    zona: string;
}

interface ListaDistribucion {
    id: string;
    nombre: string;
    creadoEn: string;
    usuarios: Usuario[];
}

interface Props {
    onGetLists?: () => Promise<DistributionList[]>;
    onUpdate?: (id: string) => void;
    onDelete?: (id: string) => Promise<void>;
}

function initials(nombre: string) {
    return nombre.split(' ').slice(0, 2).map((p) => p[0]).join('').toUpperCase();
}

//function zonas(usuarios: Usuario[]) {
//    return [...new Set(usuarios.map((u) => u.zona))];
//}

// Función para mapear User a Usuario
function mapDistributionList(distributionList: any, index: number): ListaDistribucion {
    const listaDistribucion: ListaDistribucion = {
        id: distributionList.id, // Usa el índice como id
        nombre: distributionList.name,
        creadoEn: distributionList.createdAt || '',
        usuarios: []
    }
    if(distributionList?.organization === 'm4'){
        listaDistribucion.usuarios = distributionList.users;
    }
    if(distributionList?.organization === 'arauco'){
        listaDistribucion.usuarios = distributionList.usersBasic;

    }
    return listaDistribucion;
}

const MAX_VISIBLE = 4;

export function ViewDistributionList({ onGetLists, onUpdate, onDelete }: Props) {
    const [query, setQuery] = useState('');
    const [panelLista, setPanelLista] = useState<ListaDistribucion | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [listas, setListas] = useState<ListaDistribucion[]>([]);
    const [refresh, setRefresh] = useState<boolean>(false);

    useEffect(() => {
        const fetchLists = async () => {
            try {
                const fetchLists = onGetLists ? await onGetLists() : [];
                const listaDistribucion: ListaDistribucion[] = fetchLists.map((user, index) =>
                    mapDistributionList(user, index)
                );
                setListas(listaDistribucion);
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        };
        fetchLists();
    }, [onGetLists, refresh]);
    const filtradas = useMemo(() =>
        listas.filter((l) =>
            l.nombre.toLowerCase().includes(query.toLowerCase()) ||
            l.usuarios.some((u) =>
                u.nombre.toLowerCase().includes(query.toLowerCase()) ||
                u.zona.toLowerCase().includes(query.toLowerCase())
            )
        ), [listas, query]
    );

    const handleEliminar = async (id: string) => {
        if (!onDelete) return;
        setDeletingId(id);
        try {
            await onDelete(id);
            alert('Lista eliminada exitosamente');
        } catch (error: any) {
            alert('error al eliminar lista: ' + (error?.response?.data?.message || error.message || 'Error desconocido'));
        }
        finally {
            setDeletingId(null);
            setRefresh((prev) => !prev);
        }
    };

    return (
        <div className="w-100 p-4">
            {/* Top bar */}
            <InputGroup className="mb-4">
                <FormControl
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Buscar lista..."
                />
                <InputGroup.Text className="text-muted small">
                    {filtradas.length} lista{filtradas.length !== 1 ? 's' : ''}
                </InputGroup.Text>
            </InputGroup>

            {/* Grid */}
            {filtradas.length === 0 ? (
                <div className="text-center py-5 text-muted small">
                    Sin listas que coincidan
                </div>
            ) : (
                <Row xs={1} sm={2} className="g-3">
                    {filtradas.map((lista: any) => {
                        const visible = lista.usuarios.slice(0, MAX_VISIBLE);
                        const extra = lista.usuarios.length - MAX_VISIBLE;

                        return (
                            <Col key={lista.id}>
                                <Card className="h-100">
                                    <Card.Body className="d-flex flex-column gap-3">

                                        {/* Header */}
                                        <div className="d-flex justify-content-between align-items-start gap-2">
                                            <div>
                                                <p className="mb-0 fw-medium small">{lista.nombre}</p>
                                                <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>
                                                    Creada el{' '}
                                                    {new Date(lista.creadoEn).toLocaleDateString('es-CL', {
                                                        day: '2-digit',
                                                        month: 'short',
                                                        year: 'numeric',
                                                    })}
                                                </p>
                                            </div>
                                            <div className="d-flex gap-1 flex-shrink-0">
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => setPanelLista(lista)}
                                                    title="Ver detalle"
                                                    style={{ width: 32, height: 32, padding: 0 }}
                                                >
                                                    👁
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => onUpdate?.(lista.id)}
                                                    title="Editar"
                                                    style={{ width: 32, height: 32, padding: 0 }}
                                                >
                                                    ✏️
                                                </Button>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => handleEliminar(lista.id)}
                                                    disabled={deletingId === lista.id}
                                                    title="Eliminar"
                                                    style={{ width: 32, height: 32, padding: 0 }}
                                                >
                                                    🗑
                                                </Button>
                                            </div>
                                        </div>

                                        <hr className="my-0" />

                                        {/* Chips de usuarios */}
                                        <div className="d-flex flex-wrap gap-2">
                                            {visible.map((u: any) => (
                                                <Badge
                                                    key={u.rut}
                                                    pill
                                                    bg="light"
                                                    text="dark"
                                                    className="d-flex align-items-center gap-1 border fw-normal px-2 py-1"
                                                >
                          <span
                              className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-medium"
                              style={{ width: 20, height: 20, fontSize: 10, flexShrink: 0 }}
                          >
                            {initials(u.nombre)}
                          </span>
                                                    <span style={{ fontSize: '0.75rem' }}>
                            {u.nombre.split(' ')[0]}
                          </span>
                                                </Badge>
                                            ))}
                                            {extra > 0 && (
                                                <Badge
                                                    pill
                                                    bg="light"
                                                    text="secondary"
                                                    className="border fw-normal px-2 py-1"
                                                    style={{ cursor: 'pointer', fontSize: '0.75rem' }}
                                                    onClick={() => setPanelLista(lista)}
                                                >
                                                    +{extra} más
                                                </Badge>
                                            )}
                                        </div>

                                    </Card.Body>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            )}

            {/* Modal detalle */}
            <Modal show={!!panelLista} onHide={() => setPanelLista(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fs-6 fw-medium">{panelLista?.nombre}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    <p className="text-muted small mb-3">
                        {panelLista?.usuarios.length} usuario
                        {panelLista?.usuarios.length !== 1 ? 's' : ''}
                    </p>
                    {panelLista?.usuarios.map((u: any, i: number) => (
                        <div
                            key={u.rut}
                            className={`d-flex align-items-center gap-3 py-2 ${
                                i < panelLista.usuarios.length - 1 ? 'border-bottom' : ''
                            }`}
                        >
                            <div
                                className="rounded-circle bg-primary bg-opacity-25 text-primary d-flex align-items-center justify-content-center fw-medium flex-shrink-0"
                                style={{ width: 32, height: 32, fontSize: 12 }}
                            >
                                {initials(u.nombre)}
                            </div>
                            <div className="flex-grow-1 overflow-hidden">
                                <p className="mb-0 small fw-medium">{u.nombre}</p>
                                <p className="mb-0 text-muted text-truncate" style={{ fontSize: '0.7rem' }}>
                                    {u.rut} · {u.correo}
                                </p>
                            </div>
                            <Badge bg="primary" className="bg-opacity-10 text-primary fw-normal flex-shrink-0">
                                {u.zona}
                            </Badge>
                        </div>
                    ))}
                </Modal.Body>
            </Modal>
        </div>
    );
}